import styled from "styled-components";

import { clamp } from "@styles/utils/conversion.style";
import { colors } from "@styles/vars/colors.style";
import { mq } from "@styles/vars/media-queries.style";

export const LeadershipMain = styled.section`
  position: relative;
  width: 100%;
  overflow: hidden;
  padding: 7rem 0;
  background-color: ${colors.white};
  color: ${colors.charcoal};

  ${mq.tabletL} {
    padding: 9rem 0;
  }
`;

export const LeadershipHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 3rem;
  margin-bottom: 5rem;

  ${mq.tabletL} {
    margin-bottom: clamp(5rem, 5vw, 8rem);
  }
`;

export const LeadershipTitle = styled.h2`
  display: flex;
  align-items: baseline;
  gap: 0.8rem;
  margin: 0;
  color: ${colors.charcoal};
  font-family: Signifier;
  font-weight: 400;
  will-change: transform, opacity;

  ${clamp("font-size", 22, 54)};

  strong {
    font-family: National2;
    font-weight: 900;
  }
`;

export const LeadershipNavigation = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.8rem;
`;

export const LeadershipNavigationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 8rem;
  min-height: 3.6rem;
  padding: 0.8rem 1.6rem;
  border: 0.1rem solid ${colors.charcoal};
  border-radius: 999rem;
  background: transparent;
  color: ${colors.charcoal};
  font-family: National2;
  font-size: 1.2rem;
  font-weight: 400;
  line-height: 1;
  text-transform: uppercase;
  cursor: pointer;
  transition:
    background-color 250ms ease,
    color 250ms ease,
    opacity 250ms ease;

  &:hover {
    background-color: ${colors.charcoal};
    color: ${colors.white};
  }

  &:focus-visible {
    outline: 0.2rem solid ${colors.pastelGreen};
    outline-offset: 0.3rem;
  }

  &.swiper-button-disabled {
    opacity: 0.35;
    pointer-events: none;
  }

  @media (max-width: 767px) {
    min-width: auto;
    padding-inline: 1.2rem;
    font-size: 1rem;
  }
`;

export const LeadershipSlider = styled.div`
  width: 100%;


  .swiper {
    width: 100%;
    overflow: visible;
  }

  .swiper-wrapper {
    align-items: flex-start;
  }

  .swiper-slide {
    width: calc((100% - 1.6rem) / 1.5);
    height: auto;
    flex-shrink: 0;
  }

  @media (min-width: 768px) {
    .swiper-slide {
      width: calc((100% - 4.8rem) / 2.5);
    }
  }

  ${mq.tabletL} {

    .swiper-slide {
      width: calc((100% - 14.4rem) / 4.5);
    }
  }
`;

export const LeadershipCard = styled.article`
  position: relative;
  width: 100%;
  height: 100%;
  isolation: isolate;
  transition: transform 300ms ease;

  ${mq.tabletL} {
    z-index: 1;

    &:hover,
    &:focus-within {
      z-index: 2;
      transform: scale(1.15);
    }
  }
`;

export const LeadershipCardLink = styled.a`
  display: block;
  width: 100%;
  color: inherit;
  text-decoration: none;

  &:focus-visible {
    outline: 0.2rem solid ${colors.pastelGreen};
    outline-offset: 0.5rem;
  }
`;

export const LeadershipCardImage = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 0.82;
  overflow: hidden;
  background-color: #f1f1f1;
  transform-origin: center bottom;
  transition: transform 500ms
    cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;

  .gatsby-image-wrapper {
    display: block;
    width: 100%;
    height: 100%;
  }

  img {
    filter: grayscale(1);
    transform: scale(1);
    transition:
    filter 500ms ease,
    transform 500ms cubic-bezier(0.22, 1, 0.36, 1);
    will-change: transform, filter;
  }

  ${mq.tabletL} {

    ${LeadershipCard}:hover & img,
    ${LeadershipCard}:focus-within & img {
      filter: grayscale(0);
    }
  }
`;

export const LeadershipCardContent = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 2rem;
  padding-top: 2rem;
  transform-origin: center top;
  transition: transform 500ms
    cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;

  ${mq.tabletL} {
    ${LeadershipCard}:hover &,
    ${LeadershipCard}:focus-within & {
      transform: scale(1.02);
    }
  }
`;

export const LeadershipCardName = styled.h3`
  margin: 0;
  color: ${colors.charcoal};
  font-family: National2;
  font-size: clamp(2rem, 1.5vw, 2.8rem);
  font-weight: 900;
`;

export const LeadershipCardRole = styled.p`
  margin: 0.5rem 0 0;
  color: ${colors.charcoal};
  font-family: Signifier;
  font-size: clamp(1.8rem, 1.3vw, 2.5rem);
  font-weight: 400;
`;

export const LeadershipCardArrow = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  margin-right: 25px;
  margin-top: 0.2rem;
  color: ${colors.charcoal};
  opacity: 0;
  transform: translateY(0.8rem);
  transition: transform 300ms ease;

  svg {
    width: 100%;
    height: 100%;

    path {
      stroke-width: 0.75px;
    }
  }

  ${LeadershipCard}:hover &,
  ${LeadershipCard}:focus-within & {
    opacity: 1;
  }
`;